import { yupResolver } from "@hookform/resolvers/yup"
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Typography
} from "@mui/material"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Controller, useForm, useWatch } from "react-hook-form"
import * as yup from "yup"

import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { PRIMARY } from "@constants/colors"
import { useCustomerPageContext } from "@contexts/customer"
import { accountGetCustomerPaymentCard } from "@domain/customer/infos"
import {
  accountChangeCustomerProduct,
  accountGetNewPackageCostInfo
} from "@domain/customer/plan_billing"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import {
  PaymentCardInfo,
  PaymentMethodEnum,
  Product
} from "__generated__/graphql"

const paymentDetailPadding = 1.5

type UpgradePlanDialogProps = {
  isOpen: boolean
  setClose: () => void
  products?: Product[]
}

type UpgradePlanFormData = {
  newPlanId: string
  selectedSeatCount: number
  paymentMethod: PaymentMethodEnum
  paymentMethodId?: string
  promotionCode?: string
  isYearly: boolean
}

type CalculatedPriceData = {
  totalCost?: number
  feeCharge?: number
  couponDiscount?: number
  remindCost?: number
  aiAssistantCost?: number
}

type RowDataProps = {
  label: string
  value: string | number
  color?: string
  isBoldLabel?: boolean
  isBoldValue?: boolean
}

function getPlanRank(name?: string | null) {
  const normalizedName = (name || "").toLowerCase()
  if (["free trial", "freetrial"].includes(normalizedName)) {
    return 0
  }
  if (normalizedName === "start") {
    return 1
  }
  if (normalizedName === "grow") {
    return 2
  }
  if (normalizedName === "scale") {
    return 3
  }
  if (normalizedName === "special") {
    return 4
  }
  return -1
}

function toNumber(value?: number | null) {
  return Number(value || 0)
}

function toMoney(value?: number | null) {
  return toNumber(value).toFixed(2)
}

function RowData({
  label,
  value,
  color,
  isBoldLabel,
  isBoldValue = true
}: RowDataProps) {
  return (
    <Stack
      pt={paymentDetailPadding}
      px={paymentDetailPadding}
      direction={"row"}
      justifyContent={"space-between"}
      gap={1}
    >
      <Typography fontWeight={isBoldLabel ? "bold" : "normal"}>
        {label}
      </Typography>
      <Typography color={color} fontWeight={isBoldValue ? "bold" : "normal"}>
        {value}
      </Typography>
    </Stack>
  )
}

export default function UpgradePlanDialog({
  isOpen,
  setClose,
  products
}: UpgradePlanDialogProps) {
  const { translate, currentCustomer } = useCustomerPageContext()
  const queryClient = useQueryClient()

  const customerId = currentCustomer?._id
  const currentPackage = currentCustomer?.package
  const currentPackageName = currentPackage?.name
  const currentPeriodMode = (currentPackage?.periodMode || "").toLowerCase()
  const currentIsYearly = currentPeriodMode.includes("year")

  const addOnUser = useMemo(
    () => currentPackage?.addOns.find((addOn) => addOn.name === "User"),
    [currentPackage?.addOns]
  )

  const addOnAiAssistant = useMemo(
    () => currentPackage?.addOns.find((addOn) => addOn.name === "Ai Assistant"),
    [currentPackage?.addOns]
  )

  const minimumSeatNumber = Math.max(
    1,
    toNumber(currentPackage?.usages?.cPerson as number | null)
  )
  const maxUserSeats = Math.max(minimumSeatNumber, 100)

  const seatOptions = useMemo(() => {
    const options: number[] = []
    for (let i = minimumSeatNumber; i <= maxUserSeats; i++) {
      options.push(i)
    }
    return options
  }, [maxUserSeats, minimumSeatNumber])

  const availablePlans = useMemo(
    () =>
      (products || []).filter(
        (product): product is Product =>
          Boolean(product?._id) && Boolean(product?.name)
      ),
    [products]
  )

  const selectablePlans = useMemo(() => {
    return availablePlans.filter((plan) => {
      const currentRank = getPlanRank(currentPackageName)
      const nextRank = getPlanRank(plan.name)
      const isSamePlanYearlyUpgrade =
        plan.name === currentPackageName && !currentIsYearly
      const isHigherPlan = nextRank > currentRank
      const isUnknownPlan = nextRank < 0 && plan.name !== currentPackageName

      return isSamePlanYearlyUpgrade || isHigherPlan || isUnknownPlan
    })
  }, [availablePlans, currentIsYearly, currentPackageName])

  const getDefaultPlanId = useCallback(() => {
    const upgradeCandidates = selectablePlans
      .filter(
        (plan) => getPlanRank(plan.name) > getPlanRank(currentPackageName)
      )
      .sort((a, b) => getPlanRank(a.name) - getPlanRank(b.name))

    if (upgradeCandidates.length > 0) {
      return upgradeCandidates[0]._id || ""
    }

    if (!currentIsYearly) {
      const samePlan = selectablePlans.find(
        (plan) => plan.name === currentPackageName
      )
      if (samePlan?._id) {
        return samePlan._id
      }
    }

    return selectablePlans[0]?._id || ""
  }, [currentIsYearly, currentPackageName, selectablePlans])

  const schema: yup.ObjectSchema<UpgradePlanFormData> = yup
    .object({
      newPlanId: yup.string().required("Target plan is required"),
      selectedSeatCount: yup.number().required().min(1),
      paymentMethod: yup
        .mixed<PaymentMethodEnum>()
        .oneOf([PaymentMethodEnum.BankTransfer, PaymentMethodEnum.CreditCard])
        .required("Payment method is required"),
      paymentMethodId: yup.string().when("paymentMethod", {
        is: PaymentMethodEnum.CreditCard,
        then: (schema) => schema.required("Card is required"),
        otherwise: (schema) => schema.optional()
      }),
      promotionCode: yup.string().optional(),
      isYearly: yup.boolean().required()
    })
    .required()

  const { control, getValues, reset, setValue } = useForm<UpgradePlanFormData>({
    resolver: yupResolver(schema),
    mode: "all",
    shouldUnregister: false,
    defaultValues: {
      newPlanId: "",
      selectedSeatCount: maxUserSeats,
      paymentMethodId: "",
      paymentMethod: PaymentMethodEnum.CreditCard,
      promotionCode: "",
      isYearly: currentIsYearly
    }
  })

  const [step, setStep] = useState(1)
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false)
  const [loadingPrice, setLoadingPrice] = useState(false)
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState<PaymentCardInfo[]>([])
  const [calculatedData, setCalculatedData] = useState<CalculatedPriceData>()
  const [appliedPromotionCode, setAppliedPromotionCode] = useState<string>()
  const [ipAddress, setIPAddress] = useState("127.0.0.1")

  const watchedPlanId = useWatch({ control, name: "newPlanId" })
  const selectedSeatCountValue = useWatch({
    control,
    name: "selectedSeatCount"
  })
  const selectedPaymentType = useWatch({ control, name: "paymentMethod" })
  const selectedPaymentMethodId = useWatch({ control, name: "paymentMethodId" })
  const isYearly = useWatch({ control, name: "isYearly" })
  const promotionCode = useWatch({ control, name: "promotionCode" })

  const [stickyPlanId, setStickyPlanId] = useState<string>("")

  const selectedPlanId = watchedPlanId || stickyPlanId

  const selectedSeatCount = Number(selectedSeatCountValue || maxUserSeats)

  const selectedPlan = useMemo(
    () => availablePlans.find((plan) => plan._id === selectedPlanId),
    [availablePlans, selectedPlanId]
  )

  const isSelectedPlanAllowed = useMemo(
    () => selectablePlans.some((plan) => plan._id === selectedPlanId),
    [selectablePlans, selectedPlanId]
  )

  const paymentMethodOptions = useMemo(
    () =>
      paymentMethods
        .filter((card) => Boolean(card._id))
        .map((card) => ({
          value: card._id as string,
          label: `${card.cardAlias || "Card"} / ${card.lastFourDigits || "----"}`
        })),
    [paymentMethods]
  )

  const selectedPaymentMethod = useMemo(
    () => paymentMethods.find((card) => card._id === selectedPaymentMethodId),
    [paymentMethods, selectedPaymentMethodId]
  )

  const isCreditCardPayment =
    selectedPaymentType === PaymentMethodEnum.CreditCard

  const isUpgrade = useMemo(() => {
    if (!selectedPlan) return true

    if (selectedPlan.name === currentPackageName) {
      if (Boolean(isYearly) === currentIsYearly) {
        return false
      }

      return Boolean(isYearly)
    }

    return getPlanRank(selectedPlan.name) > getPlanRank(currentPackageName)
  }, [currentIsYearly, currentPackageName, isYearly, selectedPlan])

  const hasPlanOrPeriodChange = useMemo(() => {
    if (!selectedPlan) return false

    return (
      selectedPlan.name !== currentPackageName ||
      Boolean(isYearly) !== currentIsYearly
    )
  }, [currentIsYearly, currentPackageName, isYearly, selectedPlan])

  const calculatePrice = useCallback(
    async (promotion?: string) => {
      if (!customerId || !selectedPlan?._id || selectedSeatCount < 1) {
        return
      }

      setLoadingPrice(true)
      const { isSuccess, data } = await accountGetNewPackageCostInfo({
        customerId,
        isYearly: Boolean(isYearly),
        newPackId: selectedPlan._id,
        code: promotion || null,
        userCount: Number(selectedSeatCount)
      })

      if (isSuccess && data) {
        setCalculatedData({
          totalCost: toNumber(data.totalCost),
          feeCharge: toNumber(data.feeCharge),
          couponDiscount: toNumber(data.couponDiscount),
          remindCost: toNumber(data.remindCost),
          aiAssistantCost: toNumber(data.aiAssistantCost)
        })
      }
      setLoadingPrice(false)
    },
    [customerId, isYearly, selectedPlan?._id, selectedSeatCount]
  )

  const fetchCustomerPaymentMethods = useCallback(async () => {
    if (!customerId) return

    setLoadingPaymentMethods(true)
    const { isSuccess, data, errorMessage } =
      await accountGetCustomerPaymentCard({
        customerId
      })
    if (!isSuccess) {
      showErrorToast(String(errorMessage || "Failed to fetch payment methods"))
      setPaymentMethods([])
      setLoadingPaymentMethods(false)
      return
    }

    const cards = (data?.cards || []).filter((card): card is PaymentCardInfo =>
      Boolean(card)
    )
    setPaymentMethods(cards)

    const defaultCard = cards.find((card) => card.default) || cards[0]
    if (defaultCard?._id) {
      setValue("paymentMethodId", defaultCard._id, { shouldValidate: true })
    }

    setLoadingPaymentMethods(false)
  }, [customerId, setValue])

  const fetchIpAddress = useCallback(async () => {
    try {
      const response = await fetch("https://geolocation-db.com/json/")
      if (!response.ok) {
        return
      }

      const data: { IPv4?: string } = await response.json()
      if (data.IPv4) {
        setIPAddress(data.IPv4)
      }
    } catch {
      // keep localhost fallback
    }
  }, [])

  const hasInitializedCurrentOpenRef = useRef(false)

  useEffect(() => {
    if (!isOpen) {
      hasInitializedCurrentOpenRef.current = false
      return
    }

    if (hasInitializedCurrentOpenRef.current) {
      return
    }

    hasInitializedCurrentOpenRef.current = true
    const defaultPlanId = getDefaultPlanId()

    reset({
      newPlanId: defaultPlanId,
      selectedSeatCount: maxUserSeats,
      paymentMethodId: "",
      paymentMethod: PaymentMethodEnum.CreditCard,
      promotionCode: "",
      isYearly: currentIsYearly
    })
    setStickyPlanId(defaultPlanId)
    setStep(1)
    setAppliedPromotionCode(undefined)
    setCalculatedData(undefined)
    fetchCustomerPaymentMethods()
    fetchIpAddress()
  }, [
    currentIsYearly,
    fetchCustomerPaymentMethods,
    fetchIpAddress,
    getDefaultPlanId,
    isOpen,
    maxUserSeats,
    reset
  ])

  useEffect(() => {
    if (watchedPlanId) {
      setStickyPlanId(watchedPlanId)
      return
    }

    if (!watchedPlanId && stickyPlanId && !getValues("newPlanId")) {
      setValue("newPlanId", stickyPlanId, { shouldValidate: true })
    }
  }, [getValues, setValue, stickyPlanId, watchedPlanId])

  useEffect(() => {
    if (!isCreditCardPayment) {
      setValue("paymentMethodId", "", { shouldValidate: true })
      return
    }

    const hasSelectedCard = Boolean(getValues("paymentMethodId"))
    if (hasSelectedCard) {
      return
    }

    const defaultCard =
      paymentMethods.find((card) => card.default) || paymentMethods[0]
    if (defaultCard?._id) {
      setValue("paymentMethodId", defaultCard._id, { shouldValidate: true })
    }
  }, [getValues, isCreditCardPayment, paymentMethods, setValue])

  useEffect(() => {
    if (!isOpen || !selectedPlan?._id) return

    calculatePrice(appliedPromotionCode)
  }, [appliedPromotionCode, calculatePrice, isOpen, selectedPlan?._id])

  const endDate = currentPackage?.endDate
    ? new Date(currentPackage.endDate as string)
    : null
  const timeDifference = endDate
    ? Math.max(
        0,
        Math.ceil(
          (endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
        )
      )
    : 0

  const totalCurrentSeatCount =
    toNumber(currentPackage?.limits?.cPerson as number | null) +
    toNumber(addOnUser?.quantity as number | null)

  const currentTotalPrice =
    toNumber(currentPackage?.price as number | null) * totalCurrentSeatCount
  const newUnitPrice = Boolean(isYearly)
    ? toNumber(selectedPlan?.yearlyPrice as number | null)
    : toNumber(selectedPlan?.monthlyPrice as number | null)
  const newSeatTotalPrice = newUnitPrice * selectedSeatCount
  const newPrice = Math.max(toNumber(calculatedData?.feeCharge), 0)
  const hasAiAssistantCost = toNumber(calculatedData?.aiAssistantCost) > 0

  const isStepOneValid = Boolean(
    selectedPlan?._id &&
    isSelectedPlanAllowed &&
    selectedSeatCount >= minimumSeatNumber &&
    hasPlanOrPeriodChange
  )
  const isStepTwoValid = isCreditCardPayment
    ? Boolean(selectedPaymentMethodId)
    : selectedPaymentType === PaymentMethodEnum.BankTransfer

  const rightButtonText =
    step < 3 ? "Next" : isUpgrade ? "Upgrade now" : "Downgrade now"
  const leftButtonText = step === 1 ? translate("cancel") : "Back"

  function handleClose() {
    if (loadingSubmit || loadingPrice || loadingPaymentMethods) {
      return
    }

    setStep(1)
    setAppliedPromotionCode(undefined)
    setCalculatedData(undefined)
    setClose()
  }

  async function onSubmit(data: UpgradePlanFormData) {
    if (!customerId || !selectedPlan?._id) {
      showErrorToast("Missing customer or target plan")
      return
    }

    if (
      data.paymentMethod === PaymentMethodEnum.CreditCard &&
      !selectedPaymentMethod?._id
    ) {
      showErrorToast("Please select a payment method")
      return
    }

    setLoadingSubmit(true)
    const { isSuccess, errorMessage } = await accountChangeCustomerProduct({
      productId: selectedPlan._id,
      isYearly: Boolean(data.isYearly),
      paymentMethod: data.paymentMethod,
      lastFourDigits:
        data.paymentMethod === PaymentMethodEnum.CreditCard
          ? selectedPaymentMethod?.lastFourDigits || undefined
          : undefined,
      customerId: customerId,
      ipAdress: ipAddress,
      code: appliedPromotionCode || null,
      userCount: Number(data.selectedSeatCount)
    })

    if (!isSuccess) {
      showErrorToast(String(errorMessage || "Plan upgrade failed"))
      setLoadingSubmit(false)
      return
    }

    await queryClient.invalidateQueries({
      queryKey: ["customer-by-id", customerId]
    })
    showSuccessToast("Customer plan updated")
    setLoadingSubmit(false)
    handleClose()
  }

  function handleApplyPromotionCode() {
    const promotion = promotionCode?.trim()
    const newCode = promotion ? promotion : undefined
    setAppliedPromotionCode(newCode)
    calculatePrice(newCode)
  }

  function handleRightButtonClick() {
    if (step < 3) {
      setStep((prevStep) => prevStep + 1)
      return
    }

    const formValues = getValues()
    void onSubmit(formValues)
  }

  function handleLeftButtonClick() {
    if (step === 1) {
      handleClose()
      return
    }

    setStep((prevStep) => prevStep - 1)
  }

  const StepOne = (
    <>
      <Typography>{translate("current_plan")}</Typography>
      <Stack
        sx={{
          backgroundColor: "#F8F8F8",
          borderRadius: 2,
          pb: 2
        }}
      >
        <RowData label="Plan" value={currentPackage?.name || "-"} />
        <RowData label="Period" value={currentPackage?.periodMode || "-"} />
        <RowData label="User Seats" value={totalCurrentSeatCount} />
        <RowData label="Days Left For Renewal" value={timeDifference} />
        <Divider />
        <RowData
          label="Current Price"
          value={`$${toMoney(currentTotalPrice)}`}
          isBoldLabel
        />
      </Stack>

      <TextInput<UpgradePlanFormData>
        type="select"
        control={control}
        name="newPlanId"
        label="Target Plan"
        placeholder="Select"
        disabled={loadingSubmit || loadingPrice}
        selectOptions={selectablePlans.map((plan) => ({
          value: plan._id || "",
          label: plan.name || "Unnamed"
        }))}
        required
      />

      <Controller
        control={control}
        name="isYearly"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(field.value)}
                onChange={(_, checked) => field.onChange(checked)}
                disabled={loadingSubmit || loadingPrice}
              />
            }
            label="Is yearly billing"
          />
        )}
      />

      <TextInput<UpgradePlanFormData>
        type="select"
        control={control}
        name="selectedSeatCount"
        label="User Seat Count"
        placeholder="Select"
        disabled={loadingSubmit || loadingPrice}
        selectOptions={seatOptions.map((seat) => ({
          value: seat,
          label: seat.toString()
        }))}
        required
      />

      <Typography>New Plan</Typography>
      <Stack
        sx={{
          backgroundColor: "#F8F8F8",
          borderRadius: 2,
          pb: 2
        }}
      >
        <RowData label="Plan" value={selectedPlan?.name || "-"} />
        <RowData
          label="Period"
          value={Boolean(isYearly) ? "Yearly" : "Monthly"}
        />
        <Divider />
        <RowData label="User Seats" value={selectedSeatCount} />
        <RowData
          label="User Seat Unit Price"
          value={`$${toMoney(newUnitPrice)}`}
        />
        <RowData
          label="User Seat Total Price"
          value={`$${toMoney(newSeatTotalPrice)}`}
        />
        {hasAiAssistantCost && (
          <>
            <Divider />
            <RowData
              label="AI Assistant Seats"
              value={
                toNumber(currentPackage?.limits?.aiAssistant as number | null) +
                toNumber(addOnAiAssistant?.quantity as number | null)
              }
            />
            <RowData
              label="AI Assistant Seat Unit Price"
              value={`$${toMoney(addOnAiAssistant?.price)}`}
            />
            <RowData
              label="AI Assistant Seat Total Price"
              value={`$${toMoney(calculatedData?.aiAssistantCost)}`}
            />
          </>
        )}
        {!!calculatedData?.remindCost && (
          <RowData
            label="Remaining From Current Plan"
            value={`-$${toMoney(calculatedData.remindCost)}`}
            color="gray"
            isBoldValue={false}
          />
        )}
        <RowData
          label="New Price"
          value={`$${toMoney(newPrice)}`}
          isBoldLabel
          color={PRIMARY}
        />
      </Stack>
      {!hasPlanOrPeriodChange && (
        <Typography color="warning.main" variant="caption">
          No change selected. Choose a different plan or billing period.
        </Typography>
      )}
      {selectablePlans.length === 0 && (
        <Typography color="warning.main" variant="caption">
          No upgrade target available for this customer.
        </Typography>
      )}
    </>
  )

  const StepTwo = (
    <>
      <Stack
        sx={{
          backgroundColor: "#F8F8F8",
          borderRadius: 2,
          pb: 2
        }}
      >
        <RowData
          label="Total Price"
          value={`$${toMoney(newPrice)}`}
          color="gray"
        />
        {!!calculatedData?.couponDiscount &&
          calculatedData.couponDiscount > 0 && (
            <>
              <RowData
                label={`Discount (${appliedPromotionCode || "-"})`}
                value={`-$${toMoney(calculatedData.couponDiscount)}`}
                color="green"
                isBoldValue={false}
              />
              <RowData
                label="Final Price"
                value={`$${toMoney(calculatedData.feeCharge)}`}
                color={PRIMARY}
              />
            </>
          )}
      </Stack>

      <TextInput<UpgradePlanFormData>
        type="select"
        control={control}
        name="paymentMethod"
        label={translate("payment_method")}
        placeholder="Select"
        disabled={loadingSubmit}
        selectOptions={[
          {
            value: PaymentMethodEnum.CreditCard,
            label: translate("payment_method_credit_card")
          },
          {
            value: PaymentMethodEnum.BankTransfer,
            label: translate("payment_method_bank_transfer")
          }
        ]}
        required
      />

      {isCreditCardPayment && (
        <TextInput<UpgradePlanFormData>
          type="select"
          control={control}
          name="paymentMethodId"
          label={translate("payment_method")}
          placeholder="Select card"
          disabled={loadingSubmit || loadingPaymentMethods}
          selectOptions={paymentMethodOptions}
          required
        />
      )}
      {isCreditCardPayment &&
        paymentMethodOptions.length === 0 &&
        !loadingPaymentMethods && (
          <Typography color="warning.main" variant="caption">
            No payment method found for this customer.
          </Typography>
        )}

      <TextInput<UpgradePlanFormData>
        control={control}
        name="promotionCode"
        label="Promotion Code"
        placeholder="Enter code"
        disabled={loadingSubmit || loadingPrice}
        TextRight={
          <Typography
            component="span"
            onClick={
              loadingSubmit || loadingPrice
                ? undefined
                : handleApplyPromotionCode
            }
            sx={{
              cursor: loadingSubmit || loadingPrice ? "default" : "pointer",
              color: PRIMARY,
              display: promotionCode ? "block" : "none"
            }}
          >
            Apply
          </Typography>
        }
      />
    </>
  )

  const StepThree = (
    <Stack justifyContent={"center"} alignItems={"center"} gap={2} py={2}>
      <Typography align="center">
        {isUpgrade
          ? "You are about to upgrade this plan."
          : "You are about to downgrade this plan."}
      </Typography>
      <Typography align="center" color="text.secondary">
        Target: {selectedPlan?.name || "-"} /{" "}
        {Boolean(isYearly) ? "Yearly" : "Monthly"}
      </Typography>
      <Typography align="center" color="text.secondary">
        Charge: ${toMoney(newPrice)}
      </Typography>
      <Typography align="center" color="text.secondary">
        Seats: {selectedSeatCount}
      </Typography>
      <Typography align="center" color="text.secondary">
        Payment Method:{" "}
        {selectedPaymentType === PaymentMethodEnum.BankTransfer
          ? translate("payment_method_bank_transfer")
          : translate("payment_method_credit_card")}
      </Typography>
    </Stack>
  )

  const content = step === 1 ? StepOne : step === 2 ? StepTwo : StepThree

  return (
    <DialogProvider
      title={isUpgrade ? "Upgrade Plan" : "Downgrade Plan"}
      leftButton={{
        text: leftButtonText,
        color: "secondary",
        onClick: handleLeftButtonClick,
        disabled: loadingSubmit || loadingPrice || loadingPaymentMethods
      }}
      rightButton={{
        text: rightButtonText,
        color: !isUpgrade && step > 1 ? "error" : "primary",
        onClick: handleRightButtonClick,
        loading: loadingSubmit,
        disabled:
          loadingSubmit ||
          loadingPrice ||
          loadingPaymentMethods ||
          (step === 1
            ? !isStepOneValid
            : step === 2
              ? !isStepTwoValid
              : !selectedPlan?._id || !isStepTwoValid)
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="medium"
    >
      <Stack rowGap={2}>
        {loadingPrice || loadingPaymentMethods ? (
          <Box
            sx={{
              minHeight: 260,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          content
        )}
      </Stack>
    </DialogProvider>
  )
}
